import './index.css'
import {
  WATERMARKING,
  DISCOVERY_SUITE,
} from '@disco/constants/billing-provider-components'
import { seatsAndTracksUpdate } from '@disco/state-management/actions/SeatsAndTracks'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import { useEnableChangeAddOns } from '@disco/state-management/selectors/addOns'
import {
  getSubscriptionExpiresAt,
  getSubscriptionPeriod,
  getSubscription,
} from '@disco/state-management/selectors/subscription'
import { UNLIMITED_WATERMARKING_QUOTA } from '@disco/utils/getWatermarkingQuota'
import merge from 'lodash/merge'
import dayjs from '@disco/utils/dayjs'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import type { ButtonAppearance } from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import DiscoModal from 'components/ui/disco-modal'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'

const AddOnsConfirmModal = (): React.ReactNode => {
  const navigate = useNavigate()
  const location = useLocation()
  // Parse query parameters from URLSearchParams
  const searchParams = new URLSearchParams(location.search)
  const query = {
    value: searchParams.get('value') || '0',
    price: searchParams.get('price') || '0',
    coupon: searchParams.get('coupon') || '',
    isUpdate: searchParams.get('isUpdate') || '0',
    type: searchParams.get('type') || '',
    isModalExplorerRoute: searchParams.get('isModalExplorerRoute') || '',
    trackId: searchParams.get('trackId') || '',
  }
  const isModalExplorerRoute = Boolean(query.isModalExplorerRoute)
  const trackId = Number(query.trackId)
  const unsubscribeAvailable = Number(query.value) === 0
  const dispatch = useDispatch()
  const disabledUpdatePaymentDetails = useSelector(
    state => !state.user?.permissions?.includes('can_change_business_settings'),
  )
  const planPeriod = useSelector(getSubscriptionPeriod)
  const expiresAt = useSelector(getSubscriptionExpiresAt)
  const subscription = useSelector(getSubscription)
  const [loading, setLoading] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState(null)
  const isUpdate = Boolean(Number(query.isUpdate))
  const isWM = query.type === WATERMARKING
  const enableChangeAddOns = useEnableChangeAddOns({})
  useEffect(() => {
    if (!subscription && enableChangeAddOns) {
      dispatch(loadBusinessSubscription())
    }
  }, [])

  const returnPath = isModalExplorerRoute ? '/' : '/settings/billing'

  const onClose = () => navigate(returnPath)

  const onUpdate = async () => {
    try {
      setError(null)
      setLoading(true)

      /*
      newValue represents quantity for the addon. 
      eg 500 tracks for Discovery Suite.
    
      Inside seatsAndTracksUpdate we convert these into the quantity format needed by the BE.
      */
      const newAddon = {
        newValue: Number(query.value),
        type: query.type as typeof WATERMARKING | typeof DISCOVERY_SUITE,
      }

      await dispatch(
        seatsAndTracksUpdate({
          items: [newAddon],
          coupon_code: query.coupon,
        }),
      )

      setConfirmed(true)
    } catch (error) {
      console.error(error)
      setLoading(false)
      setError(error)
    }

    setLoading(false)
  }

  if (!enableChangeAddOns) {
    onClose()
  }

  if (confirmed && isModalExplorerRoute) {
    navigate(`/explorer/tracks/${trackId}/instant-instrumental`)
  }

  const itemText = isWM
    ? Number(query.value) > 1
      ? 'playlists'
      : 'playlist'
    : 'tracks'
  const typeAddOnsText = isWM ? 'Watermarking' : 'Discovery Suite'
  let submitCaption = isWM
    ? 'Confirm Watermarking Subscription'
    : 'Confirm Discovery Suite Subscription'
  let submitAppearance: ButtonAppearance = 'primary'
  let title: React.ReactNode = isWM
    ? 'Confirm Watermarking add on'
    : 'Confirm Discovery Suite add on'
  const valueMessage =
    Number(query.value) >= UNLIMITED_WATERMARKING_QUOTA
      ? 'unlimited'
      : query.value
  const displayedPrice = '$' + query.price
  let description = (
    <p className="AddOnsConfirmModal_Description">
      <span>
        You’re adding <span>{isWM ? 'Watermarking' : 'Discovery Suite'}</span>
        <span>
          {' '}
          for {valueMessage} {itemText} for {displayedPrice} per{' '}
        </span>
        <span>{planPeriod === 'monthly' ? 'month' : 'year'}</span>
        <span>, including any applicable sales taxes. </span>
      </span>
      <br />
      <span>
        This will be billed{' '}
        <span>{planPeriod === 'monthly' ? 'monthly' : 'annually'}</span>
        <span>
          {' '}
          along with your base plan{' '}
          <span>{expiresAt ? dayjs(expiresAt).format('DD/MM/YYYY') : ''}</span>
          <span>.</span>
        </span>
      </span>
    </p>
  )

  if (query.coupon) {
    description = (
      <p className="AddOnsConfirmModal_Description">
        You’re adding <span>{isWM ? 'Watermarking' : 'Discovery Suite'}</span>
        <span>
          {' '}
          for {valueMessage} {itemText}.
        </span>
      </p>
    )
  }

  if (isUpdate) {
    submitCaption = 'Confirm'
    title = isWM
      ? 'Confirm Watermarking change'
      : 'Confirm Discovery Suite change'
    description = (
      <p className="AddOnsConfirmModal_Description">
        You’re changing your{' '}
        <span>{isWM ? 'Watermarking' : 'Discovery Suite'}</span>
        <span>
          {' '}
          subscription to {valueMessage} {itemText}.
        </span>
        {!query.coupon && (
          <>
            <br />
            This change will charge {displayedPrice} to your account, including
            any applicable sales taxes.
          </>
        )}
      </p>
    )
  }

  if (unsubscribeAvailable) {
    submitCaption = 'Confirm cancellation'
    submitAppearance = 'danger'
    title = 'Are you sure you want to cancel?'
    description = (
      <div className="AddOnsConfirmModal_AlertContainer">
        <Alert status="warning" icon="warning">
          {isWM ? (
            <span>
              By cancelling Watermarking, your playlists will no longer be
              watermarked on download.{' '}
            </span>
          ) : (
            <p className="AddOnsConfirmModal_AlertDescription">
              By cancelling Discovery Suite, your Catalogs will no longer be
              available and auto tags will be removed from your tracks.
            </p>
          )}
          <span>You can also </span>
          <span className="AddOnsConfirmModal_Link" onClick={onClose}>
            Downgrade.
          </span>
        </Alert>
      </div>
    )
  }

  if (loading) {
    submitCaption = 'Loading...'
  }

  let content = (
    <>
      <Heading>{title}</Heading>

      {description}

      {error && (
        <div className="AddOnsConfirmModal_AlertContainer">
          <Alert status="danger" icon="warning">
            <p className="AddOnsConfirmModal_AlertDescription">
              Payment failed.
            </p>

            <div className="AddOnsConfirmModal_RemoveSpace--12">
              <Button
                compact
                appearance="link"
                caption="Update billing info."
                link="/settings/billing/update"
                disabled={disabledUpdatePaymentDetails}
                tooltip={
                  disabledUpdatePaymentDetails
                    ? 'Only admins on your account can make changes to your plan.'
                    : undefined
                }
              />
            </div>
          </Alert>
        </div>
      )}

      <div className="AddOnsConfirmModal_ButtonContainer">
        <ButtonContainer>
          <Button caption="Close" onClick={onClose} />
          <Button
            appearance={submitAppearance}
            caption={submitCaption}
            onClick={onUpdate}
            disabled={loading}
          />
        </ButtonContainer>
      </div>
    </>
  )

  if (confirmed && !isModalExplorerRoute) {
    title = 'Subscription confirmed!'

    let quotaMessage = valueMessage
    if (!isWM) {
      // Show either as "250 tracks" or "1k tracks"
      const num_tracks = Number(query.value)
      quotaMessage =
        num_tracks > 1000 ? `${num_tracks / 1000}k` : `${num_tracks}`
    }
    description = (
      <p className="AddOnsConfirmModal_Description--Short">
        Thanks for subscribing to {typeAddOnsText}. You have {quotaMessage}{' '}
        {itemText} in your {typeAddOnsText}{' '}
        <span>{isWM ? 'Plan' : 'quota'}</span>
        <span>.</span>
      </p>
    )

    if (isUpdate) {
      title = (
        <div className="AddOnsConfirmModal_Title">
          <Icon type="check" className="AddOnsConfirmModal_Check" />{' '}
          {typeAddOnsText} Successfully Updated
        </div>
      )
      description = (
        <p className="AddOnsConfirmModal_Description--WithIcon">
          You’ve updated your {typeAddOnsText} subscription to {valueMessage}{' '}
          {itemText}.
        </p>
      )
    }

    if (unsubscribeAvailable) {
      title = `${typeAddOnsText} Successfully Cancelled`
      description = isWM ? (
        <p className="AddOnsConfirmModal_Description">
          Sorry to see you go! Your playlists will no longer be watermarked on
          download.
          <br />
          <br />
          If you change your mind about cancelling, you can re-subscribe in
          Settings &gt; Billing.
        </p>
      ) : (
        <p className="AddOnsConfirmModal_Description">
          Sorry to see you go! Your Catalogs will no longer be accessible and
          auto tags will be removed from your tracks.
          <br />
          <br />
          If you change your mind about cancelling, you can re-subscribe in
          Settings &gt; Billing.
        </p>
      )
    }

    content = (
      <>
        <Heading>{title}</Heading>
        {description}

        {!unsubscribeAvailable && !isUpdate && (
          <div className="AddOnsConfirmModal_ButtonContainer">
            <ButtonContainer>
              <Button
                appearance="primary"
                caption="Get started"
                onClick={onClose}
              />
            </ButtonContainer>
          </div>
        )}
      </>
    )
  }

  return (
    <DiscoModal onRequestClose={onClose}>
      <div className="AddOnsConfirmModal">{content}</div>
    </DiscoModal>
  )
}

export default AddOnsConfirmModal
